import { Button } from "@/components/ui/button";
import { Module } from "@/constants";
import { useModulePermissions } from "@/modules/account/hooks/useModulePermissions";
import { useCancelContractorVisitsMutation } from "@/modules/projects/components/contractors-visits/hooks/useCancelContractorVisitsMutation";
import { useDeleteContractorVisitMutation } from "@/modules/projects/components/contractors-visits/hooks/useDeleteContractorVisitMutation";
import { ReadContractorVisit } from "@/types/contractorVisit";
import { memo, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Edit, CheckCircle, XCircle, Trash2 } from "lucide-react";
import { VISIT_STATUS_CANCELLED } from "@/schemas/contractorVisit";
import ConfirmDialog from "@/components/confirm-dialog";
import { useIsVisitEditableForRegionalRepresentative } from "../hooks/useIsVisitEditableForRegionalRepresentative";

interface ContractorVisitFooterProps {
  visitData: ReadContractorVisit | undefined;
  contractorVisitId: string;
  onEditClick: () => void;
  onConfirmClick: () => void;
}

export const ContractorVisitFooter = memo(
  ({
    visitData,
    contractorVisitId,
    onEditClick,
    onConfirmClick,
  }: ContractorVisitFooterProps) => {
    const { t } = useTranslation();
    const { can_update } = useModulePermissions(Module.ContractorVisits);
    const isVisitEditableForRegionalRepresentative =
      useIsVisitEditableForRegionalRepresentative(visitData?.editable_by_pr);

    const isEditEnabled = visitData?.can_be_edited;

    const transitions = useMemo(
      () => ({
        canCancel:
          isEditEnabled &&
          Boolean(visitData?.can_be_cancelled) &&
          isVisitEditableForRegionalRepresentative,
        canConfirm:
          isEditEnabled &&
          Boolean(visitData?.can_be_confirmed) &&
          isVisitEditableForRegionalRepresentative,
      }),
      [
        isEditEnabled,
        visitData?.can_be_cancelled,
        visitData?.can_be_confirmed,
        isVisitEditableForRegionalRepresentative,
      ],
    );
    const { can_delete } = useModulePermissions(Module.ContractorVisits);
    const cancelMutation = useCancelContractorVisitsMutation(contractorVisitId);
    const deleteMutation = useDeleteContractorVisitMutation(contractorVisitId);

    const handleCancel = useCallback(() => {
      cancelMutation.mutate({ status: VISIT_STATUS_CANCELLED });
    }, [cancelMutation]);

    const handleDelete = useCallback(() => {
      deleteMutation.mutate(undefined);
    }, [deleteMutation]);

    const isPending = cancelMutation.isPending || deleteMutation.isPending;
    const showDelete =
      can_delete &&
      isVisitEditableForRegionalRepresentative &&
      visitData?.can_be_deleted;

    return (
      <div className="flex flex-col sm:flex-row gap-3 justify-between w-full">
        <div className="flex flex-col sm:flex-row gap-3">
          {transitions.canConfirm && (
            <Button
              variant="default"
              onClick={onConfirmClick}
              disabled={!transitions.canConfirm || isPending}
            >
              <CheckCircle className="h-4 w-4 mr-2" />
              {t("projectContractorVisits.actions.confirm")}
            </Button>
          )}

          {transitions.canCancel && (
            <ConfirmDialog
              title={t("projectContractorVisits.actions.cancel")}
              content={t(
                "projectContractorVisits.actions.cancelConfirmContent",
              )}
              onConfirm={handleCancel}
            >
              <Button
                variant="outline"
                disabled={!transitions.canCancel || isPending}
              >
                <XCircle className="h-4 w-4 mr-2" />
                {t("projectContractorVisits.actions.cancel")}
              </Button>
            </ConfirmDialog>
          )}

          {showDelete && (
            <ConfirmDialog
              title={t("projectContractorVisits.actions.delete")}
              content={t(
                "projectContractorVisits.actions.deleteConfirmContent",
              )}
              onConfirm={handleDelete}
            >
              <Button variant="destructive" disabled={isPending}>
                <Trash2 className="h-4 w-4 mr-2" />
                {t("projectContractorVisits.actions.delete")}
              </Button>
            </ConfirmDialog>
          )}
        </div>

        {can_update &&
          isVisitEditableForRegionalRepresentative &&
          isEditEnabled && (
            <Button variant="default" onClick={onEditClick}>
              <Edit className="w-4 h-4 mr-2" />
              {t("projectContractorVisits.actions.edit")}
            </Button>
          )}
      </div>
    );
  },
);
